// Phase 4 · Social Inbox (Facebook comments, Instagram comments & DMs).
// Reads /api/brand-os/social-inbox. Shows classification badges so Roger
// can scan ALWAYS_MANUAL items (medical/legal/crisis/pastoral/complaint)
// at a glance. Reply/Hide/Mark-handled default to TEST_MODE so no real
// Meta call fires until the operator opts in per-action.
import React, { useCallback, useEffect, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const CAT_TONE = {
  ALWAYS_MANUAL: "danger",
  AUTO_REPLY_CANDIDATE: "success",
};

const SUB_LABEL = {
  medical: "medical",
  legal: "legal",
  crisis: "CRISIS",
  pastoral: "pastoral",
  child_safety: "child safety",
  complaint: "complaint",
};

const STATUS_TONE = {
  manual_queue: "warn",
  auto_candidate: "muted",
  replied: "success",
  hidden: "muted",
  handled: "muted",
};

function RelTime({ iso }) {
  if (!iso) return null;
  const t = typeof iso === "string" ? new Date(iso) : iso;
  const now = new Date();
  const sec = Math.floor((now - t) / 1000);
  const label =
    sec < 60 ? `${sec}s ago` :
    sec < 3600 ? `${Math.floor(sec/60)}m ago` :
    sec < 86400 ? `${Math.floor(sec/3600)}h ago` :
    `${Math.floor(sec/86400)}d ago`;
  return <span style={{ color: "var(--bo-muted)" }}>{label}</span>;
}

export default function SocialInbox() {
  const [state, setState] = useState({ loading: true, error: null });
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | manual | auto | replied | hidden
  const [flash, setFlash] = useState(null);
  const [replyMap, setReplyMap] = useState({}); // itemId -> draft

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const q = new URLSearchParams();
      if (filter === "manual") q.set("category", "ALWAYS_MANUAL");
      else if (filter === "auto") q.set("category", "AUTO_REPLY_CANDIDATE");
      else if (filter === "replied") q.set("status", "replied");
      else if (filter === "hidden") q.set("status", "hidden");
      q.set("limit", "100");
      const r = await api.get(`/social-inbox?${q.toString()}`);
      setItems(r.data.items || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function doReply(it, liveMode = false) {
    const draft = (replyMap[it.id] || "").trim();
    if (!draft) {
      setFlash("Write a reply first.");
      return;
    }
    try {
      const r = await api.post(`/social-inbox/${it.id}/reply`, {
        message: draft, test_mode: !liveMode,
      });
      setFlash(liveMode
        ? `Replied live. External id: ${r.data.action.external_reply_id || "—"}`
        : `TEST_MODE: would reply via ${r.data.action.endpoint}`);
      setReplyMap((m) => ({ ...m, [it.id]: "" }));
      load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  async function doHide(it) {
    if (!window.confirm(`Hide this ${it.channel} comment?`)) return;
    try {
      await api.post(`/social-inbox/${it.id}/hide`, { test_mode: true });
      setFlash("TEST_MODE: hide payload recorded.");
      load();
    } catch (e) { setState((s) => ({ ...s, error: formatApiError(e) })); }
  }

  async function doHandled(it) {
    try {
      await api.post(`/social-inbox/${it.id}/mark-handled`, { note: null });
      setFlash("Marked handled.");
      load();
    } catch (e) { setState((s) => ({ ...s, error: formatApiError(e) })); }
  }

  const manualCount = items.filter((x) => x.classification?.category === "ALWAYS_MANUAL" && x.status === "manual_queue").length;
  const autoCount = items.filter((x) => x.status === "auto_candidate").length;

  return (
    <Shell title="Social Inbox">
      <div className="bo-page" data-testid="social-inbox-page">
        <section className="bo-card">
          <div className="bo-eyebrow">Phase 4 · Comments & DMs</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>
            Social Inbox
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 780, lineHeight: 1.55 }}>
            Facebook Page comments, Instagram comments, and Instagram DMs. Items
            touching medical, legal, crisis, pastoral, child-safety, or complaint
            topics are always routed to a human. Reply, Hide, and Mark-handled
            default to TEST_MODE — a real Meta call only fires when you tap{" "}
            <strong>Reply (live)</strong>.
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            {["all", "manual", "auto", "replied", "hidden"].map((k) => (
              <button
                key={k}
                className={`bo-btn ${filter === k ? "bo-btn-primary" : "bo-btn-ghost"}`}
                data-testid={`social-inbox-filter-${k}`}
                onClick={() => setFilter(k)}
              >
                {k === "all" ? `All (${items.length})`
                  : k === "manual" ? `Needs human (${manualCount})`
                  : k === "auto" ? `Auto candidates (${autoCount})`
                  : k[0].toUpperCase() + k.slice(1)}
              </button>
            ))}
          </div>
          {flash && (
            <div className="bo-flash" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)" }}>
              {flash}
            </div>
          )}
        </section>

        <section style={{ marginTop: 20 }}>
          <ErrorBanner error={state.error} />
          {state.loading ? <LoadingCard label="Loading inbox…" />
            : items.length === 0 ? (
              <EmptyState title="No items" description="Nothing to review." />
            ) : (
              <div style={{ display: "grid", gap: 12 }} data-testid="social-inbox-list">
                {items.map((it) => {
                  const cat = it.classification?.category;
                  const sub = it.classification?.subcategory;
                  return (
                    <div className="bo-card" key={it.id} data-testid={`inbox-item-${it.id}`}>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <Pill tone={CAT_TONE[cat] || "muted"}>{cat === "ALWAYS_MANUAL" ? "needs human" : "auto candidate"}</Pill>
                        {sub && <Pill tone="danger">{SUB_LABEL[sub] || sub}</Pill>}
                        <Pill tone="muted">{it.channel} · {it.kind}</Pill>
                        <Pill tone={STATUS_TONE[it.status] || "muted"}>{it.status}</Pill>
                        {it.classification?.intents?.length ? (
                          <Pill tone="muted">intents: {it.classification.intents.join(", ")}</Pill>
                        ) : null}
                        <div style={{ marginLeft: "auto", fontSize: 12 }}>
                          <RelTime iso={it.received_at} />
                        </div>
                      </div>
                      <div style={{ marginTop: 8, color: "var(--bo-muted)", fontSize: 13 }}>
                        <strong>{it.author_name || it.author_id || "someone"}</strong> on {it.channel}
                      </div>
                      <div style={{ marginTop: 6, lineHeight: 1.55 }}>
                        {it.text}
                      </div>
                      {it.classification?.matched_patterns?.length ? (
                        <div className="bo-mono" style={{ marginTop: 6, fontSize: 11, color: "var(--bo-muted)" }}>
                          matched: {it.classification.matched_patterns.join(" · ")}
                        </div>
                      ) : null}
                      {it.status !== "handled" && it.status !== "replied" && it.status !== "hidden" && (
                        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
                          <textarea
                            placeholder="Draft a reply… (default is TEST_MODE)"
                            data-testid={`inbox-reply-${it.id}`}
                            value={replyMap[it.id] || ""}
                            onChange={(e) => setReplyMap((m) => ({ ...m, [it.id]: e.target.value }))}
                            rows={3}
                            style={{
                              padding: "8px 10px", borderRadius: 8,
                              border: "1px solid var(--bo-border)",
                              background: "var(--bo-bg-elev)", color: "var(--bo-ink)",
                              fontFamily: "inherit", fontSize: 14,
                            }}
                          />
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button className="bo-btn bo-btn-primary"
                                    data-testid={`inbox-reply-test-${it.id}`}
                                    onClick={() => doReply(it, false)}>
                              Reply (TEST_MODE)
                            </button>
                            <button className="bo-btn bo-btn-ghost"
                                    data-testid={`inbox-reply-live-${it.id}`}
                                    onClick={() => {
                                      if (window.confirm("Fire a LIVE reply to Meta?")) doReply(it, true);
                                    }}>
                              Reply (live)
                            </button>
                            {it.kind === "comment" && (
                              <button className="bo-btn bo-btn-ghost"
                                      data-testid={`inbox-hide-${it.id}`}
                                      onClick={() => doHide(it)}>
                                Hide comment
                              </button>
                            )}
                            <button className="bo-btn bo-btn-ghost"
                                    data-testid={`inbox-handled-${it.id}`}
                                    onClick={() => doHandled(it)}>
                              Mark handled
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </section>
      </div>
    </Shell>
  );
}
